import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Button } from '@mui/material';

import { Dialog } from '../components';
import { translate } from '../utils/translations';

type InfoDialogOptions = {
  message: string;
};

type InfoDialogContextValue = {
  InfoDialog: (options: InfoDialogOptions) => Promise<boolean>;
};

type InfoDialogRequest = {
  options: InfoDialogOptions;
  resolve: (value: boolean) => void;
};

const InfoDialogContext = createContext<InfoDialogContextValue | null>(null);

export function InfoDialogProvider({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const [req, setReq] = useState<InfoDialogRequest | null>(null);

  const InfoDialog = useCallback(
    (options: InfoDialogOptions) =>
      new Promise<boolean>((resolve) => {
        setReq({ options, resolve });
      }),
    []
  );

  const close = (result: boolean) => {
    if (!req) return;
    req.resolve(result);
    setReq(null);
  };

  const value = useMemo<InfoDialogContextValue>(
    () => ({
      InfoDialog,
    }),
    [InfoDialog]
  );

  return (
    <InfoDialogContext.Provider value={value}>
      {children}
      <Dialog
        open={req !== null}
        content={req?.options.message}
        onClose={() => close(true)}
        width="800px"
        actions={[
          <Button
            key="submit"
            color="primary"
            variant="contained"
            onClick={() => close(true)}
          >
            {translate('COMMON.CLOSE')}
          </Button>,
        ]}
      />
    </InfoDialogContext.Provider>
  );
}

export const useInfoDialog = () => {
  const ctx = useContext(InfoDialogContext);
  if (!ctx)
    throw new Error('useInfoDialog must be used within InfoDialogProvider');
  return ctx.InfoDialog;
};
